import React, { useEffect, useState } from "react";
import { safeUrl } from "../utils/safeUrl";
import toast, { Toaster } from "react-hot-toast";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FiList, FiEye, FiCheck, FiX, FiMail, FiMapPin, FiExternalLink, FiFilter,
} from "react-icons/fi";

const getUserId = (row) => row?.userId ?? row?.user_id ?? row?.userID ?? null;

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const STATUS_FILTERS = [
  { value: "all",      label: "All" },
  { value: "pending",  label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const statusStyle = (status) => {
  switch (status) {
    case "approved": return "bg-green-100 text-green-700";
    case "rejected": return "bg-red-100  text-red-600";
    default:         return "bg-orange-100 text-orange-600";
  }
};

const Modal = ({ title, onClose, children, footer }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
    <div className="bg-white w-full max-w-xl rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition">
          <FiX size={17} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-6">{children}</div>
      {footer && <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">{footer}</div>}
    </div>
  </div>
);

const DetailRow = ({ label, value, children }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
    {children ?? <span className="text-sm font-medium text-gray-800 break-all">{value ?? "—"}</span>}
  </div>
);

const PioneerList = () => {
  const navigate = useNavigate();
  const [pioneers, setPioneers]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [viewedDetails, setViewedDetails]     = useState(null);
  const [replyDetails, setReplyDetails]       = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [replyLoading, setReplyLoading]       = useState(false);
  const [subject, setSubject]                 = useState("");
  const [message, setMessage]                 = useState("");
  const [addressDetails, setAddressDetails]   = useState(null);
  const [address, setAddress]                 = useState("");
  const [addressLoading, setAddressLoading]   = useState(false);
  const [currentPage, setCurrentPage]         = useState(1);
  const [lastPage, setLastPage]               = useState(1);
  const [statusFilter, setStatusFilter]       = useState("all");
  const [total, setTotal]                     = useState(0);

  useEffect(() => {
    setCurrentPage(1);
    fetchPioneers(1, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchPioneers = async (page = 1, status = statusFilter) => {
    setLoading(true);
    try {
      const token       = localStorage.getItem("token");
      const filterParam = status !== "all" ? `&status=${status}` : "";
      const res         = await fetch(`${baseUrl}/pioneer/request-list?page=${page}${filterParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.status) {
        setPioneers(result.data);
        setCurrentPage(result.pagination.currentPage);
        setLastPage(result.pagination.lastPage);
        setTotal(result.pagination.total || 0);
      } else {
        toast.error(result.message || "Failed to fetch list.");
      }
    } catch {
      toast.error("Network error.");
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const res   = await fetch(`${baseUrl}/pioneer/request-list/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (res.ok) setViewedDetails(result.data);
      else toast.error("Failed to load details");
    } catch {
      toast.error("Error loading details");
    }
  };

  const updateStatus = async (id, status) => {
    setUpdatingStatusId(id);
    try {
      const token = localStorage.getItem("token");
      const res   = await fetch(`${baseUrl}/pioneer/request/status/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (res.ok) { toast.success(`Marked as ${status}`); fetchPioneers(currentPage); }
      else toast.error("Failed to update status");
    } catch {
      toast.error("Error updating status");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const sendReply = async () => {
    if (!replyDetails?.email || !subject || !message) { toast.error("All fields are required"); return; }
    setReplyLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res   = await fetch(`${baseUrl}/pioneer/request/replay/${replyDetails.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ email: replyDetails.email, subject, message }),
      });
      const result = await res.json();
      if (res.ok) { toast.success("Reply sent!"); setReplyDetails(null); setSubject(""); setMessage(""); }
      else toast.error(result.message || "Failed to send reply");
    } catch {
      toast.error("Error sending reply");
    } finally {
      setReplyLoading(false);
    }
  };

  const handleAddressSubmit = async () => {
    if (!address) { toast.error("Address cannot be empty"); return; }
    setAddressLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res   = await fetch(`${baseUrl}/pioneer/request/address/${addressDetails.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ address }),
      });
      const result = await res.json();
      if (res.ok) { toast.success("Address saved!"); setAddressDetails(null); fetchPioneers(currentPage); }
      else toast.error(result.message || "Failed to save address");
    } catch {
      toast.error("Network error");
    } finally {
      setAddressLoading(false);
    }
  };

  const getDaysSince = (dateStr) => {
    if (!dateStr) return "—";
    const diff = Math.abs(new Date() - new Date(dateStr));
    return Math.floor(diff / (1000 * 60 * 60 * 24)) + "d";
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Heading */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <FiList size={20} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Pioneer Requests</h2>
                <p className="text-sm text-gray-400 mt-0.5">Review and manage pioneer applications</p>
              </div>
            </div>
            {total > 0 && (
              <span className="hidden sm:block text-xs text-gray-400 bg-white border border-gray-100 shadow-sm px-3 py-1.5 rounded-lg">
                {total} total requests
              </span>
            )}
          </div>

          {/* Main card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

            {/* Toolbar */}
            <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <FiFilter size={14} className="text-gray-400" />
              <span className="text-xs font-medium text-gray-500">Filter:</span>
              <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setStatusFilter(value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      statusFilter === value
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : pioneers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <FiList size={32} className="text-gray-300 mb-3" />
                <p className="text-sm">No pioneer requests found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-4 py-3 w-10">#</th>
                      <th className="px-4 py-3">Applicant</th>
                      <th className="px-4 py-3 hidden md:table-cell">Country</th>
                      <th className="px-4 py-3 hidden lg:table-cell">Profession</th>
                      <th className="px-4 py-3 text-center hidden sm:table-cell">Refers</th>
                      <th className="px-4 py-3 hidden xl:table-cell">Applied</th>
                      <th className="px-4 py-3 hidden xl:table-cell">Days</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {pioneers.map((item, index) => (
                      <tr key={item.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3.5 text-gray-400 text-xs">
                          {(currentPage - 1) * 10 + index + 1}
                        </td>
                        <td className="px-4 py-3.5">
                          {getUserId(item) ? (
                            <button
                              onClick={() => navigate(`/user-full-details/${getUserId(item)}`, { state: { from: '/pioneer-list' } })}
                              className="text-left group"
                              title="View user details"
                            >
                              <p className="font-medium text-gray-800 group-hover:text-blue-600 group-hover:underline transition">{item.firstName} {item.lastName}</p>
                              <p className="text-xs text-gray-400 group-hover:text-blue-500 truncate max-w-[180px] transition">{item.email}</p>
                            </button>
                          ) : (
                            <>
                              <p className="font-medium text-gray-800">{item.firstName} {item.lastName}</p>
                              <p className="text-xs text-gray-400 truncate max-w-[180px]">{item.email}</p>
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-gray-600 hidden md:table-cell">{item.country || "—"}</td>
                        <td className="px-4 py-3.5 text-gray-500 hidden lg:table-cell max-w-[130px] truncate">{item.profession || "—"}</td>
                        <td className="px-4 py-3.5 text-center hidden sm:table-cell">
                          <span className="inline-block bg-blue-50 text-blue-600 text-xs font-medium px-2 py-0.5 rounded-full">
                            {item.referCount ?? 0}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-gray-400 text-xs whitespace-nowrap hidden xl:table-cell">
                          {fmtDate(item.createdAt)}
                        </td>
                        <td className="px-4 py-3.5 hidden xl:table-cell">
                          <span className="text-xs text-gray-500">{getDaysSince(item.userCreatedAt)}</span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusStyle(item.status)}`}>
                            {item.status || "pending"}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center justify-center gap-1">
                            {/* View */}
                            <button
                              onClick={() => viewDetails(item.id)}
                              className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition"
                              title="View Details"
                            >
                              <FiEye size={14} />
                            </button>
                            {/* Approve */}
                            <button
                              onClick={() => updateStatus(item.id, "approved")}
                              disabled={updatingStatusId === item.id || item.status === "approved"}
                              className="p-1.5 rounded-lg text-green-500 hover:bg-green-50 disabled:opacity-40 transition"
                              title="Approve"
                            >
                              <FiCheck size={14} />
                            </button>
                            {/* Reject */}
                            <button
                              onClick={() => updateStatus(item.id, "rejected")}
                              disabled={updatingStatusId === item.id || item.status === "rejected"}
                              className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 disabled:opacity-40 transition"
                              title="Reject"
                            >
                              <FiX size={14} />
                            </button>
                            {/* Reply */}
                            <button
                              onClick={() => { setReplyDetails(item); setSubject(""); setMessage(""); }}
                              className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 transition"
                              title="Reply"
                            >
                              <FiMail size={14} />
                            </button>
                            {/* Address */}
                            <button
                              onClick={() => { setAddressDetails(item); setAddress(""); }}
                              className="p-1.5 rounded-lg text-purple-500 hover:bg-purple-50 transition"
                              title="Add Address"
                            >
                              <FiMapPin size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && pioneers.length > 0 && lastPage > 1 && (
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-gray-400">Page {currentPage} of {lastPage}</p>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => fetchPioneers(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40"
                  >
                    &laquo;
                  </button>
                  {Array.from({ length: lastPage }, (_, i) => i + 1).map((pg) => (
                    <button
                      key={pg}
                      onClick={() => fetchPioneers(pg)}
                      className={`px-3 py-1.5 text-sm border rounded-lg transition ${
                        currentPage === pg ? "bg-blue-500 text-white border-blue-500" : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      {pg}
                    </button>
                  ))}
                  <button
                    onClick={() => fetchPioneers(currentPage + 1)}
                    disabled={currentPage === lastPage}
                    className="px-3 py-1.5 text-sm border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40"
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Details Modal */}
      {viewedDetails && (
        <Modal
          title="Pioneer Application Details"
          onClose={() => setViewedDetails(null)}
          footer={
            <button onClick={() => setViewedDetails(null)} className="px-5 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
              Close
            </button>
          }
        >
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <DetailRow label="Full Name" value={`${viewedDetails.firstName} ${viewedDetails.lastName}`} />
            <DetailRow label="Email"     value={viewedDetails.email} />
            <DetailRow label="Country"   value={viewedDetails.country} />
            <DetailRow label="Profession" value={viewedDetails.profession} />
            <DetailRow label="Status">
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusStyle(viewedDetails.status)}`}>
                {viewedDetails.status || "pending"}
              </span>
            </DetailRow>
            <DetailRow label="Applied On" value={fmtDate(viewedDetails.createdAt)} />
            {viewedDetails.url && (
              <DetailRow label="URL">
                <a href={safeUrl(viewedDetails.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-500 hover:underline">
                  <FiExternalLink size={12} /> Open Link
                </a>
              </DetailRow>
            )}
            {viewedDetails.address && (
              <DetailRow label="Address" value={viewedDetails.address} />
            )}
            {viewedDetails.message && (
              <div className="col-span-2">
                <DetailRow label="Message" value={viewedDetails.message} />
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Reply Modal */}
      {replyDetails && (
        <Modal
          title={`Reply to ${replyDetails.email}`}
          onClose={() => setReplyDetails(null)}
          footer={
            <>
              <button onClick={() => setReplyDetails(null)} className="px-5 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                Cancel
              </button>
              <button
                onClick={sendReply}
                disabled={replyLoading}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg transition"
              >
                {replyLoading ? (
                  <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                ) : (
                  <><FiMail size={13} /> Send Reply</>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input
                type="text"
                disabled
                value={replyDetails.email}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Message</label>
              <ReactQuill
                value={message}
                onChange={setMessage}
                placeholder="Write your reply..."
                className="bg-white rounded-xl"
                style={{ height: "180px", marginBottom: "42px" }}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Address Modal */}
      {addressDetails && (
        <Modal
          title={`Add Address — ${addressDetails.email}`}
          onClose={() => setAddressDetails(null)}
          footer={
            <>
              <button onClick={() => setAddressDetails(null)} className="px-5 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                Cancel
              </button>
              <button
                onClick={handleAddressSubmit}
                disabled={addressLoading}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-lg transition"
              >
                {addressLoading ? (
                  <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                ) : (
                  <><FiMapPin size={13} /> Save Address</>
                )}
              </button>
            </>
          }
        >
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Address</label>
            <textarea
              rows={4}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter full address..."
              className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none"
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PioneerList;
